/* eslint-disable */
import React, { useCallback, useMemo, useState, useEffect } from 'react'
import _ from 'lodash'
import {
  RangeSlider,
  RangeSliderTrack,
  RangeSliderFilledTrack,
  RangeSliderThumb,
  HStack,
  VStack,
  Text as ChakraText,
  FormControl as ChakraFormControl,
  Box,
} from '@chakra-ui/react'
import Select from 'react-select'
import type { SingleValue } from 'react-select'

import type {
  InputFormProps as Props,
  SizeFilterValue,
} from '../../../interfaces'
import {
  getFieldsMapping,
  getMaxFileSize,
} from '../../../../utils/mediaLibrary'
import { useDebounce } from '../../../hooks/useDebounce'

const SliderLabelText = ({ text }: { text: string }) => {
  // @ts-ignore - Chakra UI complex union type limitation
  return (
    <ChakraText fontSize="smaller" color="#2d3748" fontWeight="medium">
      {text}
    </ChakraText>
  )
}

const SliderLabels = ({
  value,
  maxRange,
}: {
  value: SizeFilterValue
  maxRange: number
}) => {
  // Calculate the visual position of each thumb as a percentage of the slider width
  const minPosition = (value.range.min / maxRange) * 100
  const maxPosition = (value.range.max / maxRange) * 100
  
  // Calculate the visual distance between the two thumbs as a percentage
  const visualDistance = Math.abs(maxPosition - minPosition)
  
  // If thumbs are within 20% of slider width, show combined label
  const CLOSE_THRESHOLD = 20
  const areHandlesClose = visualDistance < CLOSE_THRESHOLD
  
  if (areHandlesClose) {
    // Show combined label centered between the two handles
    const midpointPercentage = (minPosition + maxPosition) / 2
    
    return (
      <>
        <RangeSliderThumb zIndex="none" index={0} />
        <RangeSliderThumb zIndex="none" index={1} />
        <Box
          position="absolute"
          bottom="1.25em"
          left={`${midpointPercentage}%`}
          transform="translateX(-50%)"
          whiteSpace="nowrap"
        >
          <HStack spacing="0.25em">
            <SliderLabelText text={value.range.min.toString()} />
            <SliderLabelText text="-" />
            <SliderLabelText text={value.range.max.toString()} />
            <SliderLabelText text={value.unit.toString()} />
          </HStack>
        </Box>
      </>
    )
  }
  
  // Show separate labels for each thumb
  return (
    <>
      <RangeSliderThumb zIndex="none" index={0}>
        <HStack spacing="0.25em" position="absolute" bottom="1.25em">
          <SliderLabelText text={value.range.min.toString()} />
          <SliderLabelText text={value.unit.toString()} />
        </HStack>
      </RangeSliderThumb>
      <RangeSliderThumb zIndex="none" index={1}>
        <HStack spacing="0.25em" position="absolute" bottom="1.25em">
          <SliderLabelText text={value.range.max.toString()} />
          <SliderLabelText text={value.unit.toString()} />
        </HStack>
      </RangeSliderThumb>
    </>
  )
}

const SizeInputForm = (props: Props): React.ReactElement => {
  const {
    searchQueryJson,
    validationObject,
    groupId,
    filterId,
    setSearchQueryJson,
  } = props

  const [dirty, setDirty] = useState<boolean>(false)

  const expression = useMemo(
    () => searchQueryJson.filterGroups[groupId]?.expressions[filterId],
    [searchQueryJson, groupId, filterId],
  )

  // Local state for immediate UI updates
  const [localSizeValue, setLocalSizeValue] = useState<SizeFilterValue>(() => {
    const exprValue = expression.value as SizeFilterValue
    return exprValue || { unit: 'MB', range: { min: 0, max: 100 } }
  })

  const error: string | null = useMemo(
    () => validationObject.errors[groupId][filterId],
    [validationObject, groupId, filterId],
  )

  // Debounced values for search query updates (only for slider, not unit)
  const debouncedSizeValue = useDebounce(localSizeValue, 400)

  const handleRangeChange = useCallback(
    (newValue: { min: number; max: number }) => {
      // Update local state immediately for UI responsiveness
      setLocalSizeValue(prev => ({ ...prev, range: newValue }))
      setDirty(true)
    },
    [],
  )

  const handleUnitChange = useCallback(
    (newUnit: string) => {
      // Reset range if switching to/from MB (immediate update)
      if (localSizeValue.unit !== newUnit && (localSizeValue.unit === 'MB' || newUnit === 'MB')) {
        setLocalSizeValue(prev => ({
          ...prev,
          unit: newUnit,
          range: { min: 0, max: getMaxFileSize(newUnit) }
        }))
      } else {
        setLocalSizeValue(prev => ({ ...prev, unit: newUnit }))
      }
      
      // Update global state immediately for unit changes
      const newSearchQueryJson = _.cloneDeep(searchQueryJson)
      const currentValue = newSearchQueryJson.filterGroups[groupId].expressions[filterId].value as SizeFilterValue
      
      if (localSizeValue.unit !== newUnit && (localSizeValue.unit === 'MB' || newUnit === 'MB')) {
        (newSearchQueryJson.filterGroups[groupId].expressions[filterId].value as SizeFilterValue) = {
          unit: newUnit,
          range: { min: 0, max: getMaxFileSize(newUnit) }
        }
      } else {
        (newSearchQueryJson.filterGroups[groupId].expressions[filterId].value as SizeFilterValue) = {
          ...currentValue,
          unit: newUnit
        }
      }
      
      setSearchQueryJson(newSearchQueryJson)
      setDirty(true)
    },
    [localSizeValue.unit, searchQueryJson, groupId, filterId, setSearchQueryJson],
  )

  // Sync debounced slider values with global state (unit changes are handled immediately)
  useEffect(() => {
    const newSearchQueryJson = _.cloneDeep(searchQueryJson)
    const currentValue = newSearchQueryJson.filterGroups[groupId].expressions[filterId].value as SizeFilterValue
    
    // Only update if range values have actually changed (unit is handled immediately)
    if (currentValue.range.min !== debouncedSizeValue.range.min || 
        currentValue.range.max !== debouncedSizeValue.range.max) {
      
      (newSearchQueryJson.filterGroups[groupId].expressions[filterId].value as SizeFilterValue) = {
        ...currentValue,
        range: debouncedSizeValue.range
      }
      setSearchQueryJson(newSearchQueryJson)
    }
  }, [debouncedSizeValue, searchQueryJson, groupId, filterId, setSearchQueryJson])

  // Initialize local state when expression value changes externally
  useEffect(() => {
    const expressionValue = expression.value as SizeFilterValue
    if (expressionValue) {
      setLocalSizeValue(expressionValue)
    }
  }, [expression.value])

  return (
    <ChakraFormControl width="full" isInvalid={error !== null && dirty}>
      <VStack width="full" spacing="2.5em" alignItems="center">
        <VStack width="full" alignItems="end">
          <Select
            isSearchable={false}
            options={getFieldsMapping({
              fieldName: expression.fieldName,
            }).possibleUnits?.map(unit => ({
              value: unit,
              label: unit,
            }))}
            value={{
              value: localSizeValue.unit,
              label: localSizeValue.unit,
            }}
            onChange={(
              newVal: SingleValue<{
                label: string
                value: string
              }>,
            ) => {
              newVal?.value && handleUnitChange(newVal.value)
              setDirty(true)
            }}
          />
        </VStack>

        <RangeSlider
          width="90%"
          min={0}
          max={getMaxFileSize(localSizeValue.unit)}
          value={[
            localSizeValue.range.min,
            localSizeValue.range.max,
          ]}
          onChange={values => {
            handleRangeChange({
              min: values[0],
              max: values[1],
            })
            setDirty(true)
          }}
        >
          <RangeSliderTrack>
            <RangeSliderFilledTrack />
          </RangeSliderTrack>
          <SliderLabels 
            value={localSizeValue} 
            maxRange={getMaxFileSize(localSizeValue.unit)}
          />
        </RangeSlider>
      </VStack>
    </ChakraFormControl>
  )
}

export default SizeInputForm
